<template>
  <v-container fluid fill-width class="ma-0 pa-2">
    <v-app-bar app class="primary">
      <v-icon color="white" large>library_add</v-icon>
      <v-toolbar-title class="white--text pl-3"
        ><span v-if="changeVarsMode"
          >Alter Study Dataset - Add/Remove Variables</span
        ><span v-else>Create New Study Dataset - Select Variables</span>
        <div class="subtitle-1">
          <span v-if="changeVarsMode"
            >Dataset: {{ collection == null ? '' : collection.label }}</span
          ><span v-else>Dataset: New Study Dataset (not saved)</span>
        </div>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-chip
        v-if="useAutomatedAnalysisMode"
        color="yellow"
        text-color="black"
        :disabled="!useAutomatedAnalysisMode"
        >Auto Mode ON</v-chip
      >
      <v-chip
        v-if="showSelectedVarCounts"
        :color="getNumVariablesColor(1)"
        :text-color="getNumVariablesTextColor(1)"
        class="title ma-2"
        >{{ numSubjectVars }} demographic var{{
          numSubjectVars != 1 ? 's' : ''
        }}</v-chip
      >
      <v-chip
        v-if="showSelectedVarCounts"
        :color="getNumVariablesColor(numObservationVars)"
        :text-color="getNumVariablesTextColor(numObservationVars)"
        class="title ma-2"
        >{{ numObservationVars }} observation var{{
          numObservationVars != 1 ? 's' : ''
        }}</v-chip
      >
      <v-chip
        :color="getNumSubjectsColor(numSubjects)"
        :text-color="getNumSubjectsTextColor(numSubjects)"
        class="title ma-2"
        >TOTAL SAMPLE: {{ numSubjects }} subjects</v-chip
      >

      <save-collection-btn-dialog
        :variables="variables"
        :dataset-ids="selectedDatasetIDs"
        :num-subjects-selected="numSubjects"
        :num-observation-vars-selected="numObservationVars"
        :num-subject-vars-selected="numSubjectVars"
        :automated-analysis-mode="useAutomatedAnalysisMode"
        :automated-analysis-predictor-vars="automatedAnalysisInputs"
        :automated-analysis-output-vars="automatedAnalysisOutputs"
        :collection="collection"
        :use-long-scale-names="useLongScaleNames"
        :is-longitudinal="isLongitudinal"
        @dialogOpen="dialogOpened"
        @collectionSaved="collectionSaved"
      />
    </v-app-bar>

    <analysis-tracker
      :step.sync="step"
      :substep.sync="substep"
      :automated-analysis-mode="useAutomatedAnalysisMode"
    ></analysis-tracker>

    <v-container fluid fill-width class="ma-0 pa-0 pt-2">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <v-sheet color="white" height="100%" class="rounded-lg shadow">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2"
                ><span v-if="changeVarsMode">Add/Remove Shared Variables</span
                ><span v-else>Shared Variables</span>
                <v-spacer />
                <v-chip
                  v-if="!changeVarsMode"
                  color="primary"
                  text-color="white"
                  class="mr-1"
                  :disabled="!useAutomatedAnalysisMode"
                  >Auto</v-chip
                >
                <v-switch
                  v-if="!changeVarsMode"
                  v-model="useAutomatedAnalysisMode"
                  label="Enable automated analysis mode"
                  class="pa-0 ma-0"
                  hide-details
                ></v-switch>
                <v-switch
                  v-model="useLongScaleNames"
                  label="Use long variable names"
                  class="pa-0 ma-0 pl-3"
                  hide-details
                ></v-switch>
                <v-switch
                  v-model="showScaleDisplayGroups"
                  label="Show variable groups"
                  class="pa-0 ma-0 pl-3"
                  hide-details
                ></v-switch>
              </v-card-title>
              <v-card-subtitle class="primary--text pl-3 pt-2"
                ><span v-if="changeVarsMode"
                  >Add or remove variables from an existing study dataset.</span
                ><span v-else
                  >Select variables to include in the new study dataset. At
                  least one outcome (i.e., non-demographic) variable must be
                  selected.</span
                ></v-card-subtitle
              >
            </v-card>

            <shared-variable-table
              v-if="selectedDatasets != null && selectedDatasets.length > 0"
              v-model="selected"
              :datasets="selectedDatasets"
              selectable
              :use-long-scale-names="useLongScaleNames"
              :use-automated-analysis-mode="useAutomatedAnalysisMode"
              :show-scale-display-groups="showScaleDisplayGroups"
              :collection="collection"
              @nSubjects="updateNumSubjects"
              @nObservationVars="updateNumObservationVars"
              @nSubjectVars="updateNumSubjectVars"
              @autoAnalysisInputs="updateAutoAnalysisInputs"
              @autoAnalysisOutputs="updateAutoAnalysisOutputs"
            />
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { state, actions } from '@/store/modules/datasetManager/types';
import AnalysisTracker from '@/components/common/AnalysisTracker.vue';
import SharedVariableTable from '@/components/DatasetManager/SharedVariableTable.vue';
import SaveCollectionBtnDialog from '@/components/BuildDataset/SaveCollectionBtnDialog.vue';
import { actions as collaborationActions } from '@/store/modules/collaboration/types';
import {
  colors,
  getNumSubjectsColor,
  getNumSubjectsTextColor,
  getNumVariablesColor,
  getNumVariablesTextColor,
} from '@/utils/colors';
import { getLongScaleNameDefault } from '@/utils/helpers';

export default {
  components: {
    AnalysisTracker,
    SharedVariableTable,
    SaveCollectionBtnDialog,
  },

  props: {
    // id is passed in via route parameters
    // i.e., /build?id=x&id=y
    id: {
      type: [String, Array],
      default: null,
    },
    collectionId: {
      type: Number,
      default: null,
    },
    showSelectedVarCounts: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    collectionName: '',
    activeDataset: null,
    selected: [],
    step: '1',
    substep: '1.2',
    numSubjects: 0,
    numObservationVars: 0,
    numSubjectVars: 0,
    colors: colors,
    useLongScaleNames: false,
    showScaleDisplayGroups: true,
    useAutomatedAnalysisMode: false,
    automatedAnalysisInputs: [],
    automatedAnalysisOutputs: [],
    isLongitudinal: null,
  }),
  computed: {
    ...mapState('datasetManager', {
      collection: state.COLLECTION,
      datasets: state.DATASETS,
      selectedDatasets: state.SELECTED_DATASETS,
    }),
    selectedDatasetIDs() {
      return this.selectedDatasets.map(({ id }) => id);
    },
    variables() {
      return this.selected;
    },
    // true if adding/removing variables on an existing collection
    changeVarsMode() {
      return !isNaN(this.collectionId);
    },
  },
  watch: {
    datasets() {
      this.updateSelectedDatasets();
    },
    selectedDatasets() {
      this.updateIsLongitudinal();
    },

    collection(c) {
      if (c != null) {
        if (c.id) {
          this.$store.dispatch(
            'collaboration/' + collaborationActions.SET_ACTIVE_COLLECTION,
            c
          );
        }
        let ids = {};
        c['studies'].map(s => {
          ids[+s.study_id] = 1;
        });
        let ds = this.datasets.filter(ds => ds.id in ids);
        this.selectDatasets(ds);
        this.useLongScaleNames = getLongScaleNameDefault(this.selectedDatasets);
      }
    },
  },
  created() {
    this.clearCollection();
    // load datasets
    if (this.datasets == null || this.datasets.length == 0) {
      this.fetchDatasets();
    } else if (this.collectionId != null && !Number.isNaN(this.collectionId)) {
      this.updateSelectedDatasets();
    }
  },
  mounted() {
    this.updateIsLongitudinal();
  },
  methods: {
    ...mapActions('datasetManager', {
      clearCollection: actions.CLEAR_COLLECTION,
      fetchCollection: actions.FETCH_COLLECTION,
      fetchDatasets: actions.FETCH_DATASETS,
      selectDatasets: actions.SELECT_DATASETS,
    }),
    getNumSubjectsColor,
    getNumSubjectsTextColor,
    getNumVariablesColor,
    getNumVariablesTextColor,
    updateIsLongitudinal() {
      if (this.selectedDatasets.length > 0) {
        this.isLongitudinal =
          this.selectedDatasets[0].longitudinal == 0 ? false : true;
      }
    },
    updateSelectedDatasets() {
      // select datasets based on 'collectionId' prop
      if (this.collectionId != null && !Number.isNaN(this.collectionId)) {
        // clear any previous selection
        this.selectDatasets([]);
        this.fetchCollection(this.collectionId);
      }
      // select datasets based on 'id' prop
      else {
        let ids = {};
        let id_a = Array.isArray(this.id) ? this.id : [this.id];
        id_a.map(i => {
          ids[+i] = 1;
        });
        let ds = this.datasets.filter(ds => ds.id in ids);
        this.selectDatasets(ds);
      }
      this.useLongScaleNames = getLongScaleNameDefault(this.selectedDatasets);
    },
    dialogOpened(isOpen) {
      if (isOpen) {
        this.substep = '1.3';
      } else {
        this.substep = '1.2';
      }
    },
    collectionSaved() {},
    updateNumSubjects(ns) {
      this.numSubjects = ns;
    },
    updateNumObservationVars(no) {
      this.numObservationVars = no;
    },
    updateNumSubjectVars(ns) {
      this.numSubjectVars = ns;
    },
    updateAutoAnalysisInputs(ips) {
      this.automatedAnalysisInputs = ips;
    },
    updateAutoAnalysisOutputs(ops) {
      this.automatedAnalysisOutputs = ops;
    },
  },
};
</script>

<style scoped></style>
